import { SortTh } from '../../components/SortTh';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatCents, PAYMENT_FILTER_LABELS, PAYMENT_FILTERS, PAYMENT_STATUS_LABELS, SALES_INVOICE_KIND_LABELS, SALES_INVOICE_KINDS, SALES_INVOICE_STATUS_LABELS, SALES_INVOICE_STATUSES, type OpenBalance, type Paginated, type SalesInvoiceStatus, type SalesInvoiceSummary } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { YearFilter, yearOf } from '../../components/YearFilter';
import { useSession } from '../../session';
import './invoices.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
export const invoiceDay = (d: string | null) => (d ? dateFormat.format(new Date(`${d}T12:00:00`)) : '–');
export const invoiceTone = (s: SalesInvoiceStatus) => `invoice-status invoice-status--${s}`;

/** Stand für die Anzeige: Entwurf, bei ausgestellten Rechnungen der Zahlungsstand, überfällig hat Vorrang */
export function invoiceBadge(i: Pick<SalesInvoiceSummary, 'status' | 'paymentStatus' | 'overdue'>): { text: string; className: string } {
  if (i.status === 'draft') return { text: SALES_INVOICE_STATUS_LABELS.draft, className: invoiceTone('draft') };
  if (i.paymentStatus === null) return { text: SALES_INVOICE_STATUS_LABELS.issued, className: invoiceTone('issued') };
  if (i.overdue) return { text: 'Überfällig', className: 'invoice-status invoice-status--overdue' };
  return { text: PAYMENT_STATUS_LABELS[i.paymentStatus], className: `invoice-status invoice-status--${i.paymentStatus}` };
}

/** Liste der Ausgangsrechnungen mit Suche und Filtern nach Stand und Art (KON-151) */
export function InvoicesPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const kind = params.get('kind') ?? '';
  const payment = params.get('payment') ?? '';
  // Vorgabe: das laufende Jahr
  const year = yearOf(params.get('jahr'));
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);

  const list = useQuery({
    queryKey: ['invoices-out', { q, status, kind, payment, year, page, sort }],
    queryFn: () => api<Paginated<SalesInvoiceSummary>>('GET', `/invoices-out${queryString({ q, status, kind, payment, year, page, sort, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const balance = useQuery({
    queryKey: ['open-balance', 'all'],
    queryFn: async () => (await api<{ data: OpenBalance }>('GET', '/invoices-out/open-balance')).data,
    enabled: can('invoices_out.read_amounts'),
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const onSort = (next: string) => update({ sort: next });
  const fields: FilterField[] = [
    { key: 'status', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, ...SALES_INVOICE_STATUSES.map((s) => ({ value: s, label: SALES_INVOICE_STATUS_LABELS[s] }))] },
    { key: 'payment', label: 'Zahlung', type: 'select', options: [{ value: '', label: 'Alle' }, ...PAYMENT_FILTERS.map((f) => ({ value: f, label: PAYMENT_FILTER_LABELS[f] }))] },
    { key: 'kind', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...SALES_INVOICE_KINDS.map((k) => ({ value: k, label: SALES_INVOICE_KIND_LABELS[k] }))] },
  ];
  const title = (i: SalesInvoiceSummary) => i.number ?? 'Entwurf';
  const gross = (i: SalesInvoiceSummary) => (i.grossCents === null ? '–' : formatCents(i.grossCents));

  return (
    <>
      <div className="page-head">
        <h1>Ausgangsrechnungen</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        {can('invoices_out.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/rechnungen/neu">Neue Rechnung</Link>}
      </div>
      {balance.data && (
        <p className="invoice-balance">
          <strong>Offene Posten:</strong> {balance.data.count === 0 ? 'keine' : `${balance.data.count} Rechnung${balance.data.count === 1 ? '' : 'en'} über ${formatCents(balance.data.openCents)}`}
          {balance.data.overdueCount > 0 && (
            <>
              {' · '}
              <button type="button" className="link-button invoice-balance__late" onClick={() => update({ payment: 'overdue' })}>
                davon überfällig {formatCents(balance.data.overdueCents)} ({balance.data.overdueCount})
              </button>
            </>
          )}
        </p>
      )}
      <div className="list-with-year">
        <div className="list-with-year__main">
          <FilterBar
            search={{ value: q, placeholder: 'Nummer, Betreff oder Kunde', onSubmit: (v) => update({ q: v }) }}
            fields={fields}
            values={{ status, payment, kind }}
            onChange={(key, value) => update({ [key]: value })}
            onReset={() => setParams(new URLSearchParams())}
          />
        </div>
        <YearFilter endpoint="/invoices-out/years" queryKey="invoices-out" value={params.get('jahr')} onChange={(v) => update({ jahr: v })} />
      </div>
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Rechnung" sortKey="number" sort={sort} onSort={onSort} />
              <SortTh label="Kunde" sortKey="contact" sort={sort} onSort={onSort} />
              <SortTh label="Art" sortKey="kind" sort={sort} onSort={onSort} />
              <SortTh label="Stand" sortKey="status" sort={sort} onSort={onSort} />
              <SortTh label="Datum" sortKey="issueDate" sort={sort} onSort={onSort} first="desc" />
              <SortTh label="Fällig" sortKey="dueDate" sort={sort} onSort={onSort} />
              <SortTh label="Brutto" sortKey="gross" sort={sort} onSort={onSort} first="desc" className="num" />
              <SortTh label="Offen" sortKey="open" sort={sort} onSort={onSort} first="desc" className="num" />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={8} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && (
              <tr><td colSpan={8} className="empty">{q || status || kind || payment ? 'Keine Rechnungen zu dieser Suche.' : year ? `Keine Rechnungen im Jahr ${year}. Über „Jahr“ lässt sich ein anderes Jahr wählen.` : 'Noch keine Rechnungen. Sie entstehen aus erledigten Aufträgen oder über „Neue Rechnung“.'}</td></tr>
            )}
            {list.data?.data.map((i) => (
              <ExpandRow
                key={i.id}
                summary={
                  <Summary
                    title={<Link to={`/rechnungen/${i.id}`} className="strong-link">{title(i)}</Link>}
                    aside={<span className={invoiceBadge(i).className}>{invoiceBadge(i).text}</span>}
                    line2={[i.contactName, i.subject].filter(Boolean).join(' · ')}
                    line3={[SALES_INVOICE_KIND_LABELS[i.kind], i.grossCents === null ? null : formatCents(i.grossCents), i.openCents ? `offen ${formatCents(i.openCents)}` : null].filter(Boolean).join(' · ')}
                  />
                }
              >
                <td className="cell-desktop-title">
                  <Link to={`/rechnungen/${i.id}`} className="strong-link">{title(i)}</Link>
                  {i.subject && <div className="muted small">{i.subject}</div>}
                </td>
                <td data-label="Kunde"><Link to={`/kontakte/${i.contactId}`}>{i.contactName}</Link></td>
                <td data-label="Art">{SALES_INVOICE_KIND_LABELS[i.kind]}</td>
                <td data-label="Stand"><span className={invoiceBadge(i).className}>{invoiceBadge(i).text}</span></td>
                <td data-label="Datum">{invoiceDay(i.issueDate)}</td>
                <td data-label="Fällig">{invoiceDay(i.dueDate)}</td>
                <td data-label="Brutto" className="num">{gross(i)}</td>
                <td data-label="Offen" className="num">{i.openCents === null ? '–' : formatCents(i.openCents)}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
